import type { Transcript, TranscriptSegment } from '../model/schema'

// Экспорт транскрипта файлом целиком на клиенте: сегменты уже получены
// JSON'ом, бэкенд-endpoint для этого не нужен.

export type TranscriptExportFormat = 'txt' | 'srt' | 'vtt'

function speakerLabel(s: TranscriptSegment): string {
  return s.speakerUsername ? `${s.speakerName} (@${s.speakerUsername})` : s.speakerName
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0')
}

// «H:MM:SS» для txt; часы не скрываем на длинных встречах.
function clockTimestamp(ms: number): string {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad(m, 2)}:${pad(s, 2)}` : `${m}:${pad(s, 2)}`
}

// «HH:MM:SS<sep>mmm» — таймкод субтитров (srt: запятая, vtt: точка).
function cueTimestamp(ms: number, sep: ',' | '.'): string {
  const h = Math.floor(ms / 3_600_000)
  const m = Math.floor((ms % 3_600_000) / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  const frac = ms % 1000
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)}${sep}${pad(frac, 3)}`
}

export function transcriptToTxt(t: Transcript): string {
  return t.segments
    .map((s) => `[${clockTimestamp(s.startMs)}] ${speakerLabel(s)}: ${s.text}`)
    .join('\n')
}

export function transcriptToSrt(t: Transcript): string {
  return t.segments
    .map(
      (s, i) =>
        `${i + 1}\n${cueTimestamp(s.startMs, ',')} --> ${cueTimestamp(s.endMs, ',')}\n${speakerLabel(s)}: ${s.text}\n`,
    )
    .join('\n')
}

export function transcriptToVtt(t: Transcript): string {
  const cues = t.segments
    .map(
      (s) =>
        `${cueTimestamp(s.startMs, '.')} --> ${cueTimestamp(s.endMs, '.')}\n<v ${speakerLabel(s)}>${s.text}`,
    )
    .join('\n\n')
  return `WEBVTT\n\n${cues}\n`
}

export const TRANSCRIPT_EXPORTERS: Record<
  TranscriptExportFormat,
  { render: (t: Transcript) => string; mime: string }
> = {
  txt: { render: transcriptToTxt, mime: 'text/plain' },
  srt: { render: transcriptToSrt, mime: 'application/x-subrip' },
  vtt: { render: transcriptToVtt, mime: 'text/vtt' },
}
